// src/components/resources/tools/generators/AiChat/GeminiThread.tsx

import type { JSX } from 'react'
import { cn } from '~/lib/utils'
import type { AiChatMessage, AiChatSettings } from './AiChat.types'
import { renderMessageContent, type MessageContentStyles } from './renderMessageContent'
import { GeminiLogo } from './BrandLogos'
import {
  ChevronDownIcon,
  CopyIcon,
  MicIcon,
  MoreIcon,
  PlusIcon,
  RefreshIcon,
  SendArrowIcon,
  ShareIcon,
  ThumbDownIcon,
  ThumbUpIcon,
} from './Icons'

/**
 * Pixel-faithful recreation of the Google Gemini conversation surface (2026
 * web UI): the "Gemini" model-pill header, a centred transcript with
 * right-aligned tonal user bubbles and full-width assistant turns led by the
 * four-point gradient spark, the response action row, and the rounded "Ask
 * Gemini" composer.
 */

interface ThreadProps {
  settings: AiChatSettings
  messages: AiChatMessage[]
}

function contentStyles(dark: boolean): MessageContentStyles {
  return {
    paragraph: 'my-3 first:mt-0 last:mb-0 leading-[1.75]',
    heading: 'my-3 text-[18px] font-medium first:mt-0',
    inlineCode: cn(
      'rounded px-1.5 py-0.5 font-mono text-[13.5px]',
      dark ? 'bg-[#2d2f31] text-[#c7d6f5]' : 'bg-[#e9eef6] text-[#1a4ca8]',
    ),
    codeBlock: cn('my-3 overflow-hidden rounded-xl border text-[#e6edf3]', dark ? 'border-[#3c4043] bg-[#1e1f20]' : 'border-[#dfe3e9] bg-[#1e1f20]'),
    codeBlockHeader: 'border-b border-white/10 bg-white/5 px-4 py-1.5 text-[12px] font-medium text-white/55',
    bulletMarker: dark ? 'bg-[#c4c7c5]' : 'bg-[#5f6368]',
    orderedMarker: 'font-medium',
    strong: 'font-semibold',
    link: 'text-[#0b57d0] underline decoration-1 underline-offset-2',
  }
}

// Precompute both themes once at module load — see ChatGptThread for rationale.
const CONTENT_STYLES = { light: contentStyles(false), dark: contentStyles(true) } as const

export function GeminiThread({ settings, messages }: ThreadProps): JSX.Element {
  const dark = settings.theme === 'dark'
  const styles = CONTENT_STYLES[settings.theme]

  const canvas = dark ? 'bg-[#1b1c1d] text-[#e3e3e3]' : 'bg-white text-[#1f1f1f]'
  const userBubble = dark ? 'bg-[#333537] text-[#e3e3e3]' : 'bg-[#f0f4f9] text-[#1f1f1f]'
  const muted = dark ? 'text-[#c4c7c5]' : 'text-[#5f6368]'
  const composer = dark ? 'bg-[#1e1f20]' : 'bg-[#f0f4f9]'

  return (
    <div className={cn('flex h-full flex-col font-sans antialiased', canvas)} style={{ fontFamily: '"Google Sans", system-ui, -apple-system, "Segoe UI", sans-serif' }}>
      {settings.showHeader ? (
        <header className="flex items-center justify-between px-4 py-3">
          {/* Decorative mock chrome — a <div>, not a <button> (no tab stop). */}
          <div className="flex items-center gap-1.5 rounded-lg px-2 py-1.5">
            <span className="text-[20px] font-medium">Gemini</span>
            <span className={cn('flex items-center gap-1 rounded-lg px-2 py-0.5 text-[14px]', dark ? 'bg-[#333537] text-[#c4c7c5]' : 'bg-[#f0f4f9] text-[#444746]')}>
              {settings.modelLabel}
              <ChevronDownIcon className="h-3.5 w-3.5" />
            </span>
          </div>
          {settings.showAvatars ? (
            <span className={cn('flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-semibold text-white', settings.userAvatarColor)}>
              {settings.userInitials}
            </span>
          ) : null}
        </header>
      ) : null}

      <div className="flex-1 overflow-y-auto px-4 pb-2 pt-2">
        <div className="mx-auto flex w-full max-w-[760px] flex-col gap-1">
          {messages.map(message =>
            message.role === 'user' ? (
              <div key={message.id} className="flex justify-end py-2.5">
                <div className={cn('max-w-[80%] whitespace-pre-wrap break-words rounded-[1.25rem] px-4 py-2.5 text-[16px] leading-[1.6]', userBubble)}>
                  {message.content}
                </div>
              </div>
            ) : (
              <div key={message.id} className="flex gap-4 py-4">
                {settings.showAvatars ? (
                  <GeminiLogo gradientId={`gemini-spark-${message.id}`} className="mt-0.5 h-7 w-7 shrink-0" />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="text-[16px]">{renderMessageContent(message.content, styles)}</div>
                  {settings.showActions ? (
                    <div className={cn('mt-2 flex items-center gap-1', muted)}>
                      {[ThumbUpIcon, ThumbDownIcon, ShareIcon, RefreshIcon, CopyIcon, MoreIcon].map((Icon, index) => (
                        <span key={index} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-black/5">
                          <Icon className="h-[18px] w-[18px]" />
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ),
          )}
        </div>
      </div>

      <div className="px-4 pb-4 pt-1">
        <div className="mx-auto w-full max-w-[760px]">
          <div className={cn('flex items-center gap-2 rounded-[1.75rem] px-4 py-3', composer)}>
            <span className={cn('flex h-8 w-8 items-center justify-center rounded-full', muted)}>
              <PlusIcon className="h-[20px] w-[20px]" />
            </span>
            <span className={cn('flex-1 text-[16px]', muted)}>Ask Gemini</span>
            <span className={cn('flex h-8 w-8 items-center justify-center rounded-full', muted)}>
              <MicIcon className="h-[20px] w-[20px]" />
            </span>
            <span className={cn('flex h-8 w-8 items-center justify-center rounded-full', dark ? 'text-[#c4c7c5]' : 'text-[#0b57d0]')}>
              <SendArrowIcon className="h-[20px] w-[20px]" />
            </span>
          </div>
          <p className={cn('mt-2 text-center text-[12px]', muted)}>Gemini can make mistakes, so double-check it.</p>
        </div>
      </div>
    </div>
  )
}
